import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, COMMUNITY_PHOTOS } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Newspaper, RefreshCw, Calendar, User } from 'lucide-react';

const CommunityNewsPage = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await axios.get(`${API}/news`);
        setNews(response.data);
      } catch (error) {
        console.error('Failed to fetch:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <div className="relative h-48 md:h-56">
        <img src={COMMUNITY_PHOTOS.main} alt="Killarney Community" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-plp-blue/90 to-plp-blue/70" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
              <Newspaper className="w-10 h-10" />
              Community News
            </h1>
            <p className="mt-2 text-white/80">
              Stay informed with the latest news and updates from Killarney.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {loading ? (
          <div className="flex justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-plp-blue" />
          </div>
        ) : news.length === 0 ? (
          <Card className="bg-white">
            <CardContent className="py-12 text-center">
              <Newspaper className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No news articles yet. Check back soon!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {news.map((article, idx) => (
              <Card key={article.id} className={`bg-white overflow-hidden ${idx === 0 ? 'border-l-4 border-l-plp-blue' : ''}`}>
                <CardContent className="p-0">
                  <div className={`${idx === 0 ? 'md:flex' : ''}`}>
                    {article.image_url && (
                      <div className={`${idx === 0 ? 'md:w-1/3' : ''}`}>
                        <img 
                          src={article.image_url} 
                          alt={article.title} 
                          className={`w-full ${idx === 0 ? 'h-64 md:h-full' : 'h-48'} object-cover`}
                        />
                      </div>
                    )}
                    <div className={`p-6 ${idx === 0 && article.image_url ? 'md:w-2/3' : ''}`}>
                      {idx === 0 && <span className="text-plp-blue text-sm font-semibold">LATEST</span>}
                      <h2 className={`font-heading font-bold text-slate-900 ${idx === 0 ? 'text-2xl mt-1' : 'text-xl'}`}>
                        {article.title}
                      </h2>
                      <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {article.author}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(article.created_at).toLocaleDateString('en-US', { 
                            year: 'numeric', month: 'long', day: 'numeric' 
                          })}
                        </span>
                      </div>
                      <p className={`mt-4 text-slate-600 leading-relaxed ${idx === 0 ? '' : 'line-clamp-3'}`}>
                        {article.content}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunityNewsPage;
